import React, { useState } from "react";
import ADNavbar from "../components/ADNavbar";
import SiteFooter from "../components/SiteFooter";
import { siteConfig, books, aboutBio, initiativesContent } from "../data/mock";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

/* ── Shared Shell ── */
const PageShell = ({ children }) => (
  <>
    <ADNavbar />
    {children}
    <SiteFooter />
  </>
);

/* ── Books Page ── */
export const BooksPage = () => (
  <PageShell>
    <section className="pt-28 pb-20 px-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-10">
          <ArrowLeft size={12} /> Back
        </Link>
        <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Library</p>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Published Works
        </h1>
        <p className="text-gray-400 text-sm mb-12">
          5 titles &middot;{" "}
          <a href={siteConfig.amazonAuthor} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
            View on Amazon <ArrowUpRight size={10} className="inline" />
          </a>
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.map((book) => (
            <a key={book.id} href={book.amazonUrl} target="_blank" rel="noopener noreferrer" className="group block">
              <div className="bg-white border border-gray-100 overflow-hidden transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg">
                <div className="h-64 overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img src={book.cover} alt={book.title} className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-gray-900 leading-snug" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{book.title}</h3>
                    {book.badge && <span className="text-[10px] tracking-wider uppercase text-gray-500 border border-gray-200 px-1.5 py-0.5 whitespace-nowrap flex-shrink-0">{book.badge}</span>}
                  </div>
                  {book.subtitle && <p className="text-xs text-gray-400 italic mb-3">{book.subtitle}</p>}
                  <p className="text-xs text-gray-400 leading-relaxed mb-4">{book.description.slice(0, 120)}...</p>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500"><span className="font-medium text-gray-700">{book.price}</span> &middot; {book.format}</div>
                    <ExternalLink size={12} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  </PageShell>
);

/* ── About Page (clean — single portrait) ── */
export const AboutPage = () => (
  <PageShell>
    <section className="pt-28 pb-20 px-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-10">
          <ArrowLeft size={12} /> Back
        </Link>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          About Adris Davis
        </h1>

        {/* Portrait */}
        <div className="float-left mr-8 mb-6 w-64">
          <div className="border border-gray-100 p-1.5">
            <img src={aboutBio.bmwImage} alt="Adris Davis" className="w-full h-auto" />
          </div>
        </div>

        {/* Bio */}
        <div className="text-gray-500 text-sm leading-[1.9] space-y-5">
          {aboutBio.body.split("\n\n").map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="clear-both" />

        {/* Book list */}
        <div className="mt-10 pl-6 border-l-2 border-gray-200">
          <p className="text-xs tracking-[0.2em] uppercase text-gray-400 mb-3">Published Works Include:</p>
          <ul className="space-y-1.5">
            {aboutBio.bookList.map((title, i) => (
              <li key={i} className="text-sm text-gray-600 italic" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{title}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  </PageShell>
);

/* ── Initiatives Page (with expandable Amber Alert) ── */
export const InitiativesPage = () => {
  const [expanded, setExpanded] = useState(null);

  return (
    <PageShell>
      <section className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-10">
            <ArrowLeft size={12} /> Back
          </Link>
          <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Ventures & Impact</p>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {initiativesContent.headline}
          </h1>
          <p className="text-gray-400 text-base mb-10">{initiativesContent.intro}</p>

          <div className="space-y-4">
            {initiativesContent.items.map((item, i) => (
              <div key={i} className="border border-gray-100 transition-all duration-200 hover:border-gray-200">
                <div
                  className={`p-6 ${item.expandable ? "cursor-pointer" : ""}`}
                  onClick={() => item.expandable && setExpanded(expanded === i ? null : i)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
                      <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 border ${
                        item.status === "Active" ? "text-gray-700 border-gray-400" : item.status === "Available" ? "text-gray-600 border-gray-300" : "text-gray-400 border-gray-200"
                      }`}>{item.status}</span>
                    </div>
                    {item.expandable && (
                      expanded === i ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />
                    )}
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed mt-2">{item.description}</p>
                </div>

                {/* Expanded content */}
                {item.expandable && expanded === i && item.fullContent && (
                  <div className="px-6 pb-6 border-t border-gray-50">
                    <div className="pt-5 text-sm text-gray-500 leading-[1.9] space-y-4">
                      {item.fullContent.split("\n\n").map((p, j) => (
                        <p key={j}>{p}</p>
                      ))}
                    </div>
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-6 text-xs text-gray-500 border border-gray-200 px-4 py-2 hover:border-gray-400 hover:text-gray-700 transition-all">
                        Visit on Facebook <ArrowUpRight size={10} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
};

/* ── Contact Page ── */
export const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setForm({ name: "", email: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <PageShell>
      <section className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-10">
            <ArrowLeft size={12} /> Back
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Get in Touch
          </h1>
          <p className="text-gray-400 text-base mb-10">
            For speaking engagements, collaborations, media inquiries, or general questions.
          </p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1.5">Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-0 py-2 border-0 border-b border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-gray-400 bg-transparent transition-colors" required />
            </div>
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-0 py-2 border-0 border-b border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-gray-400 bg-transparent transition-colors" required />
            </div>
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider mb-1.5">Message</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
                className="w-full px-0 py-2 border-0 border-b border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-gray-400 bg-transparent resize-none transition-colors" required />
            </div>
            <button type="submit" className="text-sm text-gray-900 border border-gray-900 px-8 py-2.5 tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all duration-300">
              Send
            </button>
          </form>
          {sent && <p className="mt-4 text-sm text-gray-500">Message sent. Thank you.</p>}
        </div>
      </section>
    </PageShell>
  );
};
